/* eslint-disable react/prop-types */
import React from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

/**
 * Multi-series bio-vitals-readings-per-day trend (029 US3) on the shared au-panel
 * design system. ONE line per active vital type (BP / Sugar / Oxygen / Salt / Water),
 * each a distinct colour with a text-labelled legend (never colour alone). Graceful
 * single-day/empty handling. Each type's daily values sum to that type's Total card.
 *
 * Props:
 *   data       — [{ date:'YYYY-MM-DD', bp:int, sugar:int, … }] ascending (only active types)
 *   types      — active type keys to draw a line for, e.g. ['bp','water']
 *   typesLabel — human label for the active vital types (caption only)
 */
const TYPE_META = {
  bp: { label: 'BP', color: '#f26b45' },
  sugar: { label: 'Sugar', color: '#2e9b87' },
  oxygen: { label: 'Oxygen', color: '#1f4d78' },
  salt: { label: 'Salt', color: '#e0a458' },
  water: { label: 'Water', color: '#3b82f6' },
};
const ALL = Object.keys(TYPE_META);
const shortDate = (d) => (d ? String(d).split('-').slice(1).reverse().join('/') : '');

const BioVitalsTrendChart = ({ data = [], types = [], typesLabel = 'all vital types' }) => {
  const enough = Array.isArray(data) && data.length >= 2;
  const activeTypes = (Array.isArray(types) && types.length) ? types.filter((t) => TYPE_META[t]) : ALL;

  return (
    <div className="au-panel" data-testid="bv-trend">
      <div className="au-panel__head">
        <h4 className="au-panel__title">Bio-vitals readings per day</h4>
        <span className="au-spacer" />
        <div className="au-legend" data-testid="bv-trend-legend">
          {activeTypes.map((t) => (
            <span key={t}><i style={{ background: TYPE_META[t].color }} />{TYPE_META[t].label}</span>
          ))}
        </div>
      </div>

      {/* What the graph shows + how each line is calculated. */}
      <p className="au-chart-note" data-testid="bv-trend-note">
        Each line is the <b>number of readings per day for that vital type</b> ({typesLabel}),
        by their activity date (UTC), for the current date range, search, subscription and watch segment.
        Each type’s daily values add up to its Total card above.
      </p>

      {enough ? (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
            <CartesianGrid stroke="#eef2f1" vertical={false} />
            <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <YAxis width={44} tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
            <Tooltip
              formatter={(value, name) => [Number(value).toLocaleString(), name]}
              labelFormatter={(l) => `Date: ${String(l).split('-').reverse().join('/')}`}
            />
            {activeTypes.map((t) => (
              <Line key={t} type="monotone" dataKey={t} name={TYPE_META[t].label}
                stroke={TYPE_META[t].color} strokeWidth={2.25} dot={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="au-chart-empty" data-testid="bv-trend-empty">
          Not enough data to plot a trend (need at least 2 days).
        </div>
      )}
    </div>
  );
};

export default BioVitalsTrendChart;
